import type { ContentKind, ContentState, PostPlanFormat } from '@wix/ai-scheduling-assistant-common-lib/utils';
import type { CompactPostPreview } from '../task-row-post-preview';

// One thing on the calendar, whichever source it came from. Every source normalizes into this shape so
// the views never branch on where an item was read.

export type CalendarItemKind = 'meeting' | 'booking' | 'task' | 'routine' | 'goal';

export const CALENDAR_ITEM_KINDS: readonly CalendarItemKind[] = ['meeting', 'booking', 'task', 'routine', 'goal'];

export interface CalendarPerson {
  name?: string;
}

/** A meeting's guest as the owner's calendar lists them, for its details. */
export interface CalendarGuest {
  name?: string;
  email?: string;
  rsvp?: 'accepted' | 'declined' | 'tentative' | 'pending';
  organizer?: boolean;
  /** The calendar's own account: the reader. */
  self?: boolean;
}

/** One of a goal's tasks under its deadline: its status, and its scheduled date when it has one. */
export interface GoalTaskRow {
  taskId: string;
  title: string;
  status: 'waiting' | 'scheduled' | 'inProgress' | 'done' | 'failed' | 'idea';
  at?: number;
}

/**
 * The social post a task carries, for its row: the drafted post's compact preview, or the planned idea when
 * nothing is drafted yet, and where the post is (`postStateOf`, common-lib).
 */
export interface CalendarPost {
  kind: ContentKind;
  /** Derived from the task alone; absent when the task does not say where its post is. */
  state?: ContentState;
  /** The drafted post (`socialPostPreviewCard`), as the island draws it compactly. */
  preview?: CompactPostPreview;
  /** A planned post not drafted yet (`postPlan`), only when there is no drafted post. */
  idea?: { network?: string; format?: PostPlanFormat; concept?: string };
  draft?: { title: string };
  /** The published post on its network, https. */
  url?: string;
}

/** What a goal's deadline carries about the work toward it. */
export interface GoalTasks {
  done: number;
  total: number;
  tasks: GoalTaskRow[];
}

export interface CalendarItem {
  /** Unique across sources: `<kind>:<source id>[:<occurrence>]`. */
  key: string;
  kind: CalendarItemKind;
  title: string;
  /** Epoch ms. */
  start: number;
  /** Epoch ms. Equal to `start` for a point in time (a task's due moment, a routine's run). */
  end: number;
  allDay: boolean;
  /** Provider civil dates, retained so DST never turns a one-day all-day event into a multi-day range. */
  allDayStartDate?: string;
  allDayEndDate?: string;
  /** A routine that runs every day. The day and week grids draw each run at its time; a month cell, which has no
   *  times, leaves it out rather than repeat it on every day. */
  repeatsDaily?: boolean;
  /** On a task that carries a social post: its preview and state. */
  post?: CalendarPost;
  opensTask?: boolean;
  /** On a goal deadline: the goal's own tasks, grouped under it. */
  goalTasks?: GoalTasks;
  /** Which account or site the item came from, for the layer it belongs to and the source line. */
  sourceId?: string;
  sourceLabel?: string;
  /** A connected account's sub-calendar the meeting is on. */
  calendar?: {
    id: string;
    name: string;
    /** The account's own main calendar, as the provider marks it. */
    primary?: boolean;
  };
  /** Where the item came from, for the mark beside its title: a calendar provider, a site's logo. */
  source?: { provider?: string; logoMediaId?: string };
  /** The colour the provider draws the item in (its own, else its calendar's), as hex. */
  color?: string;
  /** The owner's own answer to a meeting invitation. */
  rsvp?: 'accepted' | 'declined' | 'tentative' | 'pending';
  /** What Wix Bookings holds about the booking(s) on a session: who, paid, attended. */
  booking?: BookingDetails;
  /** Owner action pending — a task waiting on the owner, a booking waiting for approval. */
  needsYou?: boolean;
  /** Finished, cancelled or declined: drawn quieter. */
  settled?: boolean;
  status?: string;
  location?: string;
  people?: CalendarPerson[];
  /** Whether a meeting includes another person. */
  hasGuests?: boolean;
  /** A meeting's guests (the first ones, `guestCount` saying how many in all) and its description as plain text, for its details. */
  guests?: CalendarGuest[];
  guestCount?: number;
  description?: string;
  /** Bookings staff. */
  staff?: string[];
  /** Bookings staff resource identifiers, used to match sessions to a site's staff roster. */
  staffResourceIds?: string[];
  /** Bookings classes: places taken and total. */
  capacity?: { booked: number; total: number };
  videoLink?: string;
  /** The owner can drag it to new times: a timed meeting they organize on a calendar they can write. */
  movable?: boolean;
  workspaceId?: string;
  agentName?: string;
  /** A routine's work waiting on the owner: the routine's name, shown where an agent's task shows its agent. */
  routineName?: string;
  /** Agent work that really happened at its time: a finished task, or a routine run its run history records. */
  done?: boolean;
  /** A finished task's own result sentence, as its agent wrote it. */
  result?: string;
  /** A routine's last real run and how many tasks it started, for a run still to come. */
  lastRun?: number;
  lastRunTaskCount?: number;
  goalTitle?: string;
  /** In-app route (task, routine) or a provider-calendar link. */
  href?: string;
  external?: boolean;
  /** Public customer booking page for the service; separate from the item's navigation target. */
  bookingUrl?: string;
  /** A meeting whose title is withheld: the owner shows no titles, or the calendar shares free/busy only. */
  busy?: boolean;
  /** The booked service's fixed price: a decimal amount and an ISO 4217 currency. */
  price?: { amount: string; currency: string };
  /** The source entity's own ids, kept for the write phase (move, cancel, RSVP). */
  ref: Record<string, string | undefined>;
}

export interface BookingClient {
  /** Stable Bookings entity identity for booking-specific reads and revision-guarded writes. */
  id?: string;
  revision?: string;
  name?: string;
  /** Lower-cased Wix values: `confirmed`, `pending`, `waiting_list`, `canceled`, `declined`. */
  status?: string;
  /** `paid`, `not_paid`, `partially_paid`, `refunded`; absent when no payment applies. */
  payment?: string;
  /** `online` or `offline`. */
  paymentOption?: string;
  /** `attended` or `no_show`; absent until it is marked. */
  attendance?: string;
  /** Who made it: the `business` (the owner, in the dashboard) or the `customer`. */
  bookedBy?: string;
  /** The client's Wix contact, when the read sends it (the one-booking read does). */
  contactId?: string;
}

export interface BookingDetails {
  clients: BookingClient[];
}

/** A half-open window, epoch ms: `from <= t < to`. */
export interface CalendarRange {
  from: number;
  to: number;
}

/**
 * Whether an item is drawn as a bar across the days rather than a block on the hours: an all-day item,
 * or a timed one that lasts a day or more (a week of reserve duty entered as one long event), the way
 * Google draws it.
 */
export function drawsAsAllDay(item: Pick<CalendarItem, 'allDay' | 'start' | 'end'>): boolean {
  return item.allDay || item.end - item.start >= 86_400_000;
}

export function itemDone(item: Pick<CalendarItem, 'done' | 'post'>): boolean {
  return item.post ? item.post.state === 'done' : item.done === true;
}

export function opensTaskView(item: Pick<CalendarItem, 'kind' | 'opensTask' | 'post'>, publishTime: boolean): boolean {
  return item.kind === 'task' && (item.opensTask === true || (publishTime && !!item.post));
}

export function overlapsRange(item: Pick<CalendarItem, 'start' | 'end'>, range: CalendarRange): boolean {
  return item.start < range.to && Math.max(item.end, item.start + 1) > range.from;
}
